import { Injectable } from '@angular/core';
import { EnvironmentService } from '@innovation-os-ng/core/utils';

import { HttpClient } from '@angular/common/http';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import {
  BillingCustomerInformation,
  BillingEstimation,
  BillingInvoiceInterface,
  BillingService,
  InitiateSetupIntentResponse,
  PaymentMethodResponse
} from '../contracts/billing.service';

@Injectable({
  providedIn: 'root'
})
export class ApiBillingService implements BillingService {
  private readonly restUrl: string;
  private readonly customerInfoCache: {
    [tenantUri: string]: Subject<BillingCustomerInformation>;
  } = {};

  constructor(private readonly environmentService: EnvironmentService, private readonly httpClient: HttpClient) {
    this.restUrl = this.environmentService.getRestUrl();
  }

  /**
   *
   * @param tenantUri uri of tenant for settingup setup intent
   *
   * Sets up Setup Intent
   */

  public initiateSetupIntent(tenantUri: string): Observable<InitiateSetupIntentResponse> {
    return this.httpClient.post<InitiateSetupIntentResponse>(`${this.restUrl}/billing/payment-method/${tenantUri}/setup-intent`, {});
  }

  /**
   *
   * @param tenantUri uri of tenant for setting payment method
   * @param paymentMethodId id of the payment method
   * @param taxId tax Id
   *
   * Sets payment method for tenant
   * This method name will be renamed in future
   */
  public newSetPaymentMethod(tenantUri: string, paymentMethodId: string, setupIntentId: string): Observable<PaymentMethodResponse> {
    return this.httpClient.post<PaymentMethodResponse>(`${this.restUrl}/billing/payment-method/${tenantUri}`, {
      paymentMethodId,
      setupIntentId
    });
  }

  /**
   * @deprecated This method will be deprecated in future, use newSetPaymentMethod
   * @param tenantUri uri of tenant for setting payment method
   * @param paymentMethodId id of the payment method
   * @param taxId tax Id
   *
   * Sets payment method for tenant
   */
  public setPaymentMethod(
    tenantUri: string,
    paymentMethodId: string,
    invoiceEmailAddress: string,
    taxId: string,
    setupIntentId: string
  ): Observable<PaymentMethodResponse> {
    return this.httpClient.post<PaymentMethodResponse>(`${this.restUrl}/billing/payment-method/${tenantUri}`, {
      paymentMethodId,
      taxId,
      invoiceEmailAddress,
      setupIntentId
    });
  }

  /**
   * Submit billing form
   *
   * @param tenantUri uri of tenant for setting payment method
   * @param billingInvoice billing form value
   */
  public setBillingInvoiceMethod(tenantUri: string, billingInvoice: BillingInvoiceInterface): Observable<BillingInvoiceInterface> {
    return this.httpClient.post<BillingInvoiceInterface>(`${this.restUrl}/billing/payment-method/${tenantUri}/invoice-information`, {
      invoiceEmailAddress: billingInvoice.invoiceEmailAddress,
      invoiceName: billingInvoice.invoiceName,
      taxId: billingInvoice?.taxId,
      address: billingInvoice.address
    });
  }

  /**
   *
   * @param tenantUri uri of tenant whose payment method is to be retrieved
   *
   * Fetches payment method for tenant
   */
  public getPaymentMethod(tenantUri: string): Observable<PaymentMethodResponse> {
    return this.httpClient.get<PaymentMethodResponse>(`${this.restUrl}/billing/payment-method/${tenantUri}`);
  }

  /**
   *
   * @param tenantUri uri of tenant whose billing estimation is to be retrieved
   *
   * Fetches billing estimation for tenant
   */
  public getBillingEstimation(tenantUri: string): Observable<BillingEstimation> {
    return this.httpClient.get<BillingEstimation>(`${this.restUrl}/billing/calculate-estimation/${tenantUri}`);
  }

  /**
   *
   * @param activeUsers no of active users in tenant
   * @param activeModules active modules
   *
   * Calculates billing estimation based on Active Users and Active Modules
   */
  public calculateBillingEstimation(activeUsers: number, activeModules: Array<string>): Observable<BillingEstimation> {
    return this.httpClient.post<BillingEstimation>(`${this.restUrl}/billing/calculate-estimation`, {
      activeUsers,
      activeModules
    });
  }

  /**
   * Provides customer billing information for the given tenantUri
   *
   * @param tenantUri Tenant uri whose customer details is to be retrieved
   */
  public getCustomerInformation(tenantUri: string, updateCache = false): Observable<BillingCustomerInformation> {
    // we create a new subject if there is none
    // or if we want to clean cache we request again the data and trigger again the subject
    if (!this.customerInfoCache[tenantUri] || updateCache) {
      this.customerInfoCache[tenantUri] = this.customerInfoCache[tenantUri] || new ReplaySubject<BillingCustomerInformation>(1);

      this.httpClient.get<BillingCustomerInformation>(`${this.restUrl}/billing/customers/${tenantUri}`).subscribe((res) => {
        this.customerInfoCache[tenantUri].next(res);
      });
    }

    return this.customerInfoCache[tenantUri];
  }
}
